import http from "@/services/http";

/**
 * Serviço da salvIA (Salvia Agêntico T2.6).
 *
 * Fala com o proxy do Laravel (/api/assistant/chat -> comunicacao) e com o gate
 * de Propostas (/api/proposals/...). Escrita clínica NUNCA é direta: a salvIA
 * devolve PROPOSTAS pendentes; o médico aprova/edita/rejeita por aqui.
 */

/**
 * Envia uma pergunta à salvIA. Se `patientId` for informado, o agente já
 * trabalha nesse paciente; senão, ele pode pedir/confirmar o paciente. O
 * `historico` (turnos anteriores) dá memória de conversa ao agente; a resposta
 * traz o `patientId` em foco, que o painel fixa nos próximos turnos.
 *
 * @param {string} pergunta
 * @param {number|null} patientId
 * @param {{role: string, content: string}[]} historico
 * @returns {Promise<{reply: string, proposals: object[], patientId: number|null}>}
 */
export async function chat(pergunta, patientId = null, historico = []) {
  const { data } = await http.post("/api/assistant/chat", {
    pergunta,
    patient_id: patientId ?? null,
    historico: Array.isArray(historico) ? historico : [],
  });
  return {
    reply: data?.reply ?? "",
    proposals: Array.isArray(data?.proposals) ? data.proposals : [],
    patientId: data?.patient_id ?? null,
  };
}

/**
 * salvia-861/862: le os comandos ditos com o gatilho "Salvia" durante a consulta
 * e devolve as PROPOSTAS de escrita clinica para o medico aprovar.
 *
 * Passa pelo Laravel de proposito: e la que a transcricao e ANONIMIZADA antes de
 * ir para o modelo (nome, CPF, telefone, e-mail, datas). PII e dominio do
 * backend — o front so manda o texto e o paciente.
 *
 * Pode demorar: o agente le a transcricao inteira da consulta.
 *
 * @param {string} transcricao
 * @param {number|string} patientId
 * @param {string} [comandoAlvo] O comando que o profissional acabou de ditar.
 *   Omitido = varredura da consulta inteira (o botão e a rede do fim da
 *   consulta). Informado = resolve SÓ esse comando — sem isto, cada comando
 *   ditado traria de volta os anteriores e o prontuário receberia a mesma
 *   proposta várias vezes.
 * @returns {Promise<{proposals: object[], naoCriados: object[]}>}
 */
export async function comandosDaConsulta(transcricao, patientId, comandoAlvo) {
  const corpo = {
    transcricao,
    patient_id: patientId,
  };
  // Só vai no corpo quando existe: o backend distingue ausente de vazio, e
  // mandar "" ligaria o modo imediato com alvo nenhum.
  const alvo = String(comandoAlvo || "").trim();
  if (alvo) corpo.comando_alvo = alvo;

  const { data } = await http.post("/api/assistant/comandos-da-consulta", corpo);
  return {
    proposals: Array.isArray(data?.proposals) ? data.proposals : [],
    naoCriados: Array.isArray(data?.nao_criados) ? data.nao_criados : [],
  };
}

/**
 * Aprova a proposta -> o Laravel EXECUTA a ação real (grava o dado).
 * Ações sensíveis (tier 2) exigem `confirm=true`; sem isso o backend responde 409
 * com `requires_confirmation` e nada é executado.
 * @param {number} id
 * @param {boolean} [confirm] confirmação extra para propostas tier 2
 * @returns {Promise<object>} proposta atualizada (status 'executed' em sucesso)
 */
export async function approveProposal(id, confirm = false) {
  const { data } = await http.post(`/api/proposals/${id}/approve`, { confirm });
  return data?.data ?? data;
}

/**
 * Rejeita a proposta -> nada é gravado (status 'rejected').
 * @param {number} id
 * @returns {Promise<object>}
 */
export async function rejectProposal(id) {
  const { data } = await http.post(`/api/proposals/${id}/reject`);
  return data?.data ?? data;
}

/**
 * Edita payload/preview antes de aprovar (fica 'edited', ainda acionável).
 * @param {number} id
 * @param {object} payload novo payload (ex.: { field_key, value })
 * @param {string} [preview] novo texto do card
 * @returns {Promise<object>}
 */
export async function editProposal(id, payload, preview) {
  const body = {};
  if (payload !== undefined) body.payload = payload;
  if (preview !== undefined) body.preview = preview;
  const { data } = await http.post(`/api/proposals/${id}/edit`, body);
  return data?.data ?? data;
}

/**
 * T2.5 — sobe um laudo (PDF) e cria uma PROPOSTA de ingestão (exam_ingest): o
 * Laravel extrai via processar_laudo, preserva o PDF e devolve a proposta pendente.
 * Aprovar (approveProposal) cria o Exame. Nada é gravado antes da aprovação.
 * @param {File} file PDF do laudo
 * @param {string} examType tipo do exame (ex.: 'extractor_sangue')
 * @param {number} patientId
 * @returns {Promise<object>} proposta criada (status 'pending')
 */
export async function proposeFromExamPdf(file, examType, patientId) {
  const form = new FormData();
  form.append("file", file);
  form.append("examType", examType);
  form.append("patient_id", patientId);
  const { data } = await http.post("/api/proposals/from-exam-pdf", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data?.data ?? data;
}

/**
 * Ingestão de documento clínico (prontuário/anexo NÃO laboratorial): sobe o PDF, o
 * agente lê e cria UMA proposta por dado relevante (medicações, condições...). Nada
 * é gravado — volta { reply, proposals, patient_id } e o médico aprova item a item.
 * Laudo laboratorial usa proposeFromExamPdf (exam_ingest), não esta função.
 * @param {File} file PDF do documento
 * @param {number} patientId
 * @returns {Promise<{reply: string, proposals: object[], patientId: number|null}>}
 */
export async function ingestDocument(file, patientId) {
  const form = new FormData();
  form.append("file", file);
  form.append("patient_id", patientId);
  const { data } = await http.post("/api/assistant/ingest-document", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return {
    reply: data?.reply ?? "",
    proposals: Array.isArray(data?.proposals) ? data.proposals : [],
    patientId: data?.patient_id ?? null,
  };
}

/**
 * Lista as propostas acionáveis do médico (pending + edited), ou filtra por status.
 * @param {string|null} status
 * @returns {Promise<object[]>}
 */
export async function listProposals(status = null) {
  const { data } = await http.get("/api/proposals", {
    params: status ? { status } : {},
  });
  return Array.isArray(data?.data) ? data.data : [];
}
